import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { 
  Menu, 
  X, 
  Search, 
  Bell, 
  Home, 
  ClipboardList, 
  Shield, 
  TrendingUp,
  Settings,
  Download
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { useAuth } from '@/contexts/AuthContext'
import { useRealTimeAlerts } from '@/hooks/useRealTimeAlerts'
import { cn } from '@/lib/utils'

const mainNavItems = [
  { id: 'dashboard', title: 'Dashboard', icon: Home, href: '/dashboard', badge: 'Live' },
  { id: 'leads', title: 'Lead Review', icon: ClipboardList, href: '/leads' },
  { id: 'rules', title: 'Rules', icon: Shield, href: '/rules' },
  { id: 'analytics', title: 'Analytics', icon: TrendingUp, href: '/analytics' },
  { id: 'exports', title: 'Reports', icon: Download, href: '/exports' },
  { id: 'settings', title: 'Settings', icon: Settings, href: '/settings' }
]

const bottomTabItems = [
  { id: 'dashboard', title: 'Home', icon: Home, href: '/dashboard' },
  { id: 'leads', title: 'Leads', icon: ClipboardList, href: '/leads' },
  { id: 'alerts', title: 'Alerts', icon: Bell, href: '/alerts' },
  { id: 'more', title: 'More', icon: Menu, href: '/more' }
]

interface MobileNavigationProps {
  children: React.ReactNode
}

export function MobileNavigation({ children }: MobileNavigationProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const location = useLocation()
  const { profile, organization } = useAuth()
  const { unreadCount } = useRealTimeAlerts()

  const currentPath = location.pathname

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-background/95 backdrop-blur border-b md:hidden">
        <div className="flex items-center justify-between h-full px-4">
          {/* Hamburger Menu */}
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm" className="relative">
                <Menu className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </DrawerTrigger>
            
            <DrawerContent className="max-h-[80vh]">
              <div className="p-4 space-y-6">
                {/* User Profile */}
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback>
                      {profile?.full_name?.charAt(0) || profile?.email.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {profile?.full_name || 'User'}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {organization?.name}
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      {profile?.role}
                    </Badge>
                  </div>
                </div>

                {/* Navigation Links */}
                <nav className="space-y-2">
                  {mainNavItems.map((item) => {
                    const isActive = currentPath === item.href
                    return (
                      <NavLink
                        key={item.id}
                        to={item.href}
                        onClick={() => setDrawerOpen(false)}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg transition-colors",
                          isActive 
                            ? "bg-primary/10 text-primary" 
                            : "hover:bg-muted/50"
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="flex-1">{item.title}</span>
                        {item.badge && (
                          <Badge variant="secondary" className="text-xs">
                            {item.badge}
                          </Badge>
                        )}
                        {item.id === 'leads' && unreadCount > 0 && (
                          <Badge className="text-xs">
                            {unreadCount}
                          </Badge>
                        )}
                      </NavLink>
                    )
                  })}
                </nav>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-card rounded-lg border text-center">
                    <p className="text-sm text-muted-foreground">Today's Blocks</p>
                    <p className="text-lg font-semibold">79</p>
                  </div>
                  <div className="p-3 bg-card rounded-lg border text-center">
                    <p className="text-sm text-muted-foreground">Money Saved</p>
                    <p className="text-lg font-semibold">$2,370</p>
                  </div>
                </div>
              </div>
            </DrawerContent>
          </Drawer>

          {/* App Logo */}
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-semibold">Shield</span>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
            </Button>
            
            <Button variant="ghost" size="sm" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14 pb-16 md:pt-0 md:pb-0">
        {children}
      </main>

      {/* Bottom Tab Bar - Mobile Only */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur border-t md:hidden">
        <div className="grid grid-cols-4 h-16">
          {bottomTabItems.map((item) => {
            const isActive = currentPath === item.href
            return (
              <NavLink
                key={item.id}
                to={item.href}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 p-2 transition-colors relative",
                  isActive 
                    ? "text-primary" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.title}</span>
                
                {item.id === 'leads' && unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
                
                {item.id === 'alerts' && unreadCount > 0 && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </NavLink>
            )
          })}
        </div>
      </nav>

      {/* Search Modal - Mobile Only */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur md:hidden">
          <div className="flex items-center gap-3 p-4 border-b">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSearchOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <input
                type="search"
                placeholder="Search leads, patterns..."
                className="w-full px-3 py-2 bg-muted rounded-lg border-0 outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-muted-foreground">
              Recent searches will appear here
            </p>
          </div>
        </div>
      )}
    </div>
  )
}